import { Heart, History, LayoutDashboard, Sparkle, Star, UserRound } from '@music-vine/cadence/icons';
import Tooltip from '@/components/Tooltip';
import { Button } from '@music-vine/cadence/ui';
import { useHasRole } from '@/hooks/auth';
import { useBoardsMenuTooltip } from '@/hooks/ui/useBoardsMenuTooltip';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { twMerge } from '@/lib/twMerge';
const IconStyle = 'stroke-current size-4';
const BOARDS_TOOLTIP_DELAY_MS = 1000;
type MenuLinkProps = {
  children: React.ReactNode;
  className?: string;
} & ({
  href: string;
  onClick?: () => void;
} | {
  href?: never;
  onClick: () => void;
});
export const MenuLink = ({
  href,
  onClick,
  children,
  className
}: MenuLinkProps) => {
  const buttonClassName = twMerge('justify-start gap-2 p-1 px-3 font-normal text-sm xl:text-sm', className);
  if (href) {
    return <Button asChild className={buttonClassName} noFeedback onClick={onClick} variant="transparent">
        <Link href={href}>{children}</Link>
      </Button>;
  }
  return <Button className={buttonClassName} noFeedback onClick={onClick} variant="transparent" data-sentry-element="Button" data-sentry-component="MenuLink" data-sentry-source-file="UserMenuNav.tsx">
      {children}
    </Button>;
};
const BoardsTooltipLabel = ({
  showDismiss,
  onDismiss
}: {
  showDismiss?: boolean;
  onDismiss?: () => void;
}) => <div className="flex flex-col gap-1 p-1" data-sentry-component="BoardsTooltipLabel" data-sentry-source-file="UserMenuNav.tsx">
    <div className="font-bold font-sans text-sm">Say hello to Boards</div>
    <div className="font-sans text-sm">
      Save your favorite creative assets in one place - and keep them organized
      across your projects.
    </div>
    {!!showDismiss && <Button className="h-6 w-min px-0 py-0 text-pink-600 text-sm underline dark:text-white" onClick={onDismiss} variant="transparent">
        Dismiss
      </Button>}
  </div>;
export const UserMenuNav = () => {
  const [showTooltip, setShowTooltip] = useState(false);
  const isFree = useHasRole('Free');
  // Business is excluded from referral rewards (DEV-6507).
  const isBusiness = useHasRole('Business');
  const {
    show,
    dismiss
  } = useBoardsMenuTooltip();
  useEffect(() => {
    if (!show) {
      return;
    }
    const timeout = setTimeout(() => {
      setShowTooltip(true);
    }, BOARDS_TOOLTIP_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [show]);
  return <nav className="m-3 flex flex-col gap-3 text-white max-xl:mx-4 md:gap-1" data-sentry-component="UserMenuNav" data-sentry-source-file="UserMenuNav.tsx">
      <MenuLink href="/my/account/preferences" data-sentry-element="MenuLink" data-sentry-source-file="UserMenuNav.tsx">
        <UserRound className={IconStyle} data-sentry-element="UserRound" data-sentry-source-file="UserMenuNav.tsx" /> My account
      </MenuLink>
      <MenuLink href="/my/boards/favorites" data-sentry-element="MenuLink" data-sentry-source-file="UserMenuNav.tsx">
        <Heart className={IconStyle} data-sentry-element="Heart" data-sentry-source-file="UserMenuNav.tsx" /> Favorites
      </MenuLink>
      <MenuLink href="/my/boards" data-sentry-element="MenuLink" data-sentry-source-file="UserMenuNav.tsx">
        <Tooltip align="start" alignOffset={-10} className="max-w-56 xs:max-w-[22rem]" forceShow={!!showTooltip && !!show} label={<BoardsTooltipLabel onDismiss={dismiss} showDismiss={show} />} onCancelForceShow={dismiss} side="left" sideOffset={4} data-sentry-element="Tooltip" data-sentry-source-file="UserMenuNav.tsx">
          <div className="flex items-center gap-2">
            <LayoutDashboard className={IconStyle} data-sentry-element="LayoutDashboard" data-sentry-source-file="UserMenuNav.tsx" /> Boards
          </div>
        </Tooltip>
      </MenuLink>
      <MenuLink href="/my/downloads" data-sentry-element="MenuLink" data-sentry-source-file="UserMenuNav.tsx">
        <History className={IconStyle} data-sentry-element="History" data-sentry-source-file="UserMenuNav.tsx" /> Download history
      </MenuLink>
      <hr className="my-2 block border-0 border-gray-150 border-b-1 border-solid dark:border-gray-800" />
      <MenuLink href="/help-center" data-sentry-element="MenuLink" data-sentry-source-file="UserMenuNav.tsx">
        <svg aria-hidden="true" className={IconStyle} fill="none" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg" data-sentry-element="svg" data-sentry-source-file="UserMenuNav.tsx">
          <path d="M7 7.47401C7 7.47401 7.826 3.61001 11.213 3.08601C14.299 2.60901 17.558 4.12201 17.614 7.47401C17.661 10.221 13.522 12.097 12.586 13.852C12.214 14.55 12.226 16.569 12.226 16.569" stroke="currentColor" stroke-miterlimit="10" strokeLinecap="round" strokeWidth="2" data-sentry-element="path" data-sentry-source-file="UserMenuNav.tsx" />
          <circle cx="12.252" cy="20.8301" fill="currentColor" r="1" stroke="currentColor" stroke-miterlimit="10" strokeLinecap="round" strokeWidth="0.5" data-sentry-element="circle" data-sentry-source-file="UserMenuNav.tsx" />
        </svg>
        Help center
      </MenuLink>
      {!isBusiness && <MenuLink href="/refer-and-earn">
          <svg aria-hidden="true" className={IconStyle} fill="none" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg">
            <path d="M16.6486 3.82461C18.7461 5.92211 17.5658 10.5031 14.0125 14.0564C10.4591 17.6098 5.87814 18.79 3.78063 16.6925C1.68313 14.595 2.86335 10.0141 6.41673 6.4607C9.97011 2.90732 14.5511 1.7271 16.6486 3.82461ZM16.6486 3.82461L19.933 7.10817C22.031 9.20617 20.851 13.7872 17.297 17.3402C13.743 20.8932 9.163 22.0732 7.065 19.9762L3.609 16.5202" stroke="currentColor" strokeMiterlimit="10" strokeWidth="2" />
          </svg>
          Refer & earn
        </MenuLink>}
      <MenuLink href="/about" data-sentry-element="MenuLink" data-sentry-source-file="UserMenuNav.tsx">
        <Sparkle className={IconStyle} data-sentry-element="Sparkle" data-sentry-source-file="UserMenuNav.tsx" /> About Uppbeat
      </MenuLink>
      {!isFree && <MenuLink href="/pricing">
          <Star className={IconStyle} /> Plans
        </MenuLink>}
    </nav>;
};